import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import imageFadeIn from '../modifiers/image-fade-in';
import posterUrl from '../helpers/poster-url';
import fullResolutionUrl from '../helpers/full-resolution-url';
import { field, asString, parseEnrichedData } from '../utils/enriched-data';
import type Lightbox from '../services/lightbox';

interface Signature {
  Args: {
    item: unknown;
    title?: string;
    emoji?: string;
  };
  Blocks: {
    default: [];
  };
}

/**
 * Full-width cover hero for item detail pages (Letterboxd-style).
 * Cover extends behind the mobile header (used with @noSpacer on MobileHeader).
 * Below the cover: title on the left, poster on the right (overlapping cover).
 * Yields a block for metadata/badges below the title.
 */
export default class ItemCoverHero extends Component<Signature> {
  @service declare lightbox: Lightbox;

  get enrichedData(): Record<string, unknown> | null {
    return parseEnrichedData(field(this.args.item, 'enrichedData'));
  }

  get posterPath(): string {
    return asString(this.enrichedData?.['posterPath']);
  }

  get posterSrc(): string {
    if (this.posterPath) {
      return this.posterPath.startsWith('http')
        ? this.posterPath
        : `https://image.tmdb.org/t/p/w300${this.posterPath}`;
    }
    return posterUrl(this.args.item);
  }

  get backdropPath(): string {
    return asString(this.enrichedData?.['backdropPath']);
  }

  /** Backdrop image for cover: TMDB backdrop at w1280, or empty */
  get coverSrc(): string {
    if (this.backdropPath) {
      return this.backdropPath.startsWith('http')
        ? this.backdropPath
        : `https://image.tmdb.org/t/p/w1280${this.backdropPath}`;
    }
    return '';
  }

  get hasCover(): boolean {
    return this.coverSrc !== '';
  }

  get hasPoster(): boolean {
    const src = this.posterSrc;
    return Boolean(src) && !src.startsWith('/images/placeholders/');
  }

  @action
  openLightbox(): void {
    const title = this.args.title;
    if (!title) return;

    const fullRes = fullResolutionUrl(this.args.item);
    if (!fullRes) return;
    this.lightbox.open(fullRes, title);
  }

  <template>
    <div class="-mx-4 sm:-mx-6 lg:mx-0">
      {{! Cover — taller on mobile (h-72/h-80) to account for fixed header
          overlay; shorter on desktop (lg:h-64) where header is hidden }}
      <div
        class="lg:rounded-t-box relative h-72 w-full overflow-hidden sm:h-80 md:h-80 lg:h-64"
      >
        {{#if this.hasCover}}
          <img
            {{imageFadeIn}}
            src={{this.coverSrc}}
            alt=""
            aria-hidden="true"
            class="h-full w-full object-cover opacity-0 transition-opacity duration-500"
            loading="lazy"
          />
        {{else if this.hasPoster}}
          {{! Blurred poster/artwork fallback as cover }}
          <img
            {{imageFadeIn}}
            src={{this.posterSrc}}
            alt=""
            aria-hidden="true"
            class="h-full w-full scale-110 object-cover opacity-0 blur-xl transition-opacity duration-500"
            loading="lazy"
          />
        {{else}}
          <div class="bg-base-300 h-full w-full"></div>
        {{/if}}
        {{! Gradient overlay — strong at the bottom so overlapping title is readable }}
        <div
          class="from-base-100 pointer-events-none absolute inset-0 bg-gradient-to-t from-50% via-transparent to-transparent"
        ></div>
      </div>

      {{! Title + Poster section — pulled up into the cover gradient with -mt }}
      <div
        class="relative -mt-24 px-4 sm:-mt-28 sm:px-6
          {{if this.hasPoster 'pb-20 sm:pb-28'}}"
      >
        {{! Poster: positioned on the right side of this section }}
        {{#if this.hasPoster}}
          <div class="absolute top-0 right-4 sm:right-6">
            <button
              type="button"
              class="ring-base-100 cursor-pointer rounded-lg border-0 bg-transparent p-0 ring-4"
              {{on "click" this.openLightbox}}
            >
              <img
                {{imageFadeIn}}
                src={{this.posterSrc}}
                alt={{@title}}
                class="h-36 w-auto rounded-lg opacity-0 shadow-lg transition-opacity duration-300 hover:opacity-80 sm:h-44"
                loading="lazy"
              />
            </button>
          </div>
        {{/if}}

        {{! Title area (left side, padded to avoid poster) }}
        <div class="pt-2 {{if this.hasPoster 'pr-28 sm:pr-40'}}">
          {{#if @title}}
            <h1 class="text-2xl font-bold sm:text-3xl">
              {{#if @emoji}}
                <span aria-hidden="true">{{@emoji}}</span>
              {{/if}}
              {{@title}}
            </h1>
          {{/if}}
          {{yield}}
        </div>
      </div>
    </div>
  </template>
}
